import jQuery from 'jquery';
import {createAction} from 'redux-actions';

import {Resolution} from 'topmeteo/general/actions';
import {popState as defaultPopState, baseChangeRegion, startRequest} from 'topmeteo/general/actionCreators';

import {Location, StartHour} from './actions';
import {buildURL} from './url';

export const popState = nextState => (dispatch, getState) => {
    const {locations} = getState();
    const {locationID} = nextState;

    if (locationID in locations) return defaultPopState(nextState)(dispatch, getState);

    /* location has been deleted */
    window.location.reload(true);
};

export const changeRegion = id => baseChangeRegion(id, buildURL);

const changePositionAction = createAction(Location.position);

const addLocation = createAction(Location.add);

const changeLocationAction = createAction(Location.change);

export const changeLocation = location => (dispatch, getState) => {
    const {regionID, locations, locationID, ...state} = getState();

    if (typeof location === 'object') {
        dispatch(addLocation(location));
    } else {
        /* If this is a yet unknown location we set change to false to not set it as the
           current location in the state. When it would be set many components will have
           missing properties of this location and the render will lead to an error. */
        location = locations[location] || {id: location, change: false};
    }

    /* change is true unless explicitly set to false before */
    const {id, region, change = true} = location;

    if (id === locationID) return;

    if (change && region === regionID) {
        dispatch(changeLocationAction(id));
        dispatch(startRequest(0));
        dispatch(hideMapAction);
    } else {
        window.location.href = buildURL({...state, locationID: id, regionID: region || regionID});
    }
};

export const changePosition = (x, y, width, height) => (dispatch, getState) => {
    const {regionID: region, position, coordinateFetchURL} = getState();

    dispatch(changePositionAction({x: x / width * 100, y: y / height * 100}));

    jQuery.ajax(coordinateFetchURL, {
        method: 'GET',
        dataType: 'json',
        data: {
            region,
            width,
            height,
            x,
            y,
        },
        success: ({success, message, ...location}) => {
            if (success) {
                dispatch(changeLocation(location.id));
            } else {
                dispatch(changePositionAction(position));
            }
        },
    });
};

export const switchLocation = (location, callback) => (dispatch, getState) => {
    const {
        offset,
        resolution,
        startHour: hour,
        regionID: region,
        showFavouritesOnly,
        switchLocationURL,
    } = getState();

    const favorites = +showFavouritesOnly;

    jQuery.ajax(switchLocationURL, {
        data: {
            region,
            location,
            offset,
            hour,
            resolution,
            favorites,
        },
        dataType: 'json',
        success: ({success, url, html}) => {
            if (success) {
                window.location = url;
            } else {
                callback(html);
            }
        },
    });
};

const baseMapAction = createAction(Location.map);

const showMapAction = baseMapAction(true);

const hideMapAction = baseMapAction(false);

export const toggleMap = (dispatch, getState) => {
    const {showMap} = getState();

    return dispatch(showMap ? hideMapAction : showMapAction);
};

const editLocationAction = createAction(Location.edit, (id, name) => ({id, name}));

export const editLocation = (id, name) => (dispatch, getState) => {
    const {locations, editLocationURL, CSRFMiddlewareToken} = getState();
    const {poiID, name: oldName} = locations[id];

    dispatch(editLocationAction(id, name));

    /* revert the change on error */
    const error = () => dispatch(editLocationAction(id, oldName));

    jQuery.ajax(editLocationURL, {
        method: 'POST',
        dataType: 'json',
        data: {
            poi: poiID,
            name: name,
            csrfmiddlewaretoken: CSRFMiddlewareToken,
        },
        error,
        success: ({success, ...data}) => {
            if (success) {
                dispatch(editLocationAction(id, data.name));
            } else {
                error(data);
            }
        },
    });
};

const favouriteAction = createAction(Location.favourite, (id, isFavourite) => ({id, isFavourite}));

export const toggleFavourite = id => (dispatch, getState) => {
    const {locations, starLocationURL, CSRFMiddlewareToken} = getState();
    const {isFavourite, poiID} = locations[id];

    dispatch(favouriteAction(id, !isFavourite));

    /* revert the change on error */
    const error = () => dispatch(favouriteAction(id, isFavourite));

    jQuery.ajax(starLocationURL, {
        method: 'POST',
        dataType: 'json',
        data: {
            poi: poiID,
            is_favorite: !isFavourite,
            csrfmiddlewaretoken: CSRFMiddlewareToken,
        },
        error,
        success: ({success, ...data}) => {
            if (!success) {
                error(data);
            }
        },
    });
};

export const changeResolution = createAction(Resolution.change);

export const changeStartHour = createAction(StartHour.change);

const deleteLocationAction = createAction(Location.delete);

export const deleteLocation = id => (dispatch, getState) => {
    const {locations, deleteLocationURL, CSRFMiddlewareToken} = getState();
    const location = locations[id];

    dispatch(deleteLocationAction(id));

    jQuery.ajax(deleteLocationURL, {
        method: 'POST',
        dataType: 'json',
        data: {
            poi: location.poiID,
            csrfmiddlewaretoken: CSRFMiddlewareToken,
        },
        /* revert the change on error */
        error: () => dispatch(addLocation(location)),
    });
};

const moveLocationAction = createAction(Location.move, (targetIndex, destinationIndex) => ({targetIndex, destinationIndex}));

export const moveLocation = (location, destination) => (dispatch, getState) => {
    const {locations: {favorites}, moveLocationURL, CSRFMiddlewareToken} = getState();

    const targetIndex = favorites.indexOf(location.id);
    const destinationIndex = favorites.indexOf(destination.id);

    dispatch(moveLocationAction(targetIndex, destinationIndex));

    jQuery.ajax(moveLocationURL, {
        method: 'POST',
        dataType: 'json',
        data: {
            poi: location.poiID,
            position: destinationIndex + 1, // index is 0 based, position is 1 based
            csrfmiddlewaretoken: CSRFMiddlewareToken,
        },
        /* revert the change on error */
        error: () => dispatch(moveLocationAction(destinationIndex, targetIndex)),
    });
};
